import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ICommentsDatasource } from '../../comments/datasources/comments_datasource';
import { CommentServant } from '../../comments/servants/comment_servant';
import { APCourse } from '../../global/builders/ap_course_builder';
import { APCourseGroup, IAPCourseGroupMeta } from '../../global/builders/ap_course_group_builder';
import {
  APCRGenericSlot,
  APGenericSlot,
  APGenericSlotBuilder,
  CourseGroupSlot,
  CourseSlot,
  WildcardSlot
} from '../../global/builders/ap_generic_slot_builder';
import { APPlannedCourse, IAPPlannedCourseMeta } from '../../global/builders/ap_planned_course_builder';
import {
  APProgramSlot,
  IAPProgramSlotMeta,
  NCRProgramSlot,
  SingleCourseProgramSlot
} from '../../global/builders/ap_program_slot_builder';
import { APTerm } from '../../global/builders/ap_term_builder';
import { IApplicationConfiguration } from '../../global/commands/initialize_user_activity_tracking_command';
import { IAPCoursesDatasource } from '../../global/datasources/ap_courses_datasource';
import { IAPPlannedCourseDataSource } from '../../global/datasources/ap_planned_course_datasource';
import { IAPPlannedCourseGroupDatasource } from '../../global/datasources/ap_planned_course_group_datasource';
import { IAPTermsDatasource } from '../../global/datasources/ap_terms_datasource';
import { APModel } from '../../global/models/ap_model';
import { APCourseServant } from '../../global/servants/ap_course_servant';
import { APMinGradeServant } from '../../global/servants/ap_min_grade_servant';
import { APPlannedCourseServant } from '../../global/servants/ap_planned_course_servant';
import { APProgramSlotServant } from '../../global/servants/ap_program_slot_servant';
import { APTermServant } from '../../global/servants/ap_term_servant';
import { LargeCourseGroupModel } from '../../plan/components/large_course_group/models/large_course_group_model';
import { LargeCourseGroupSearchResultModel } from '../../plan/components/large_course_group/models/large_course_group_search_result_model';
import { APDegreeMapQueryHelper } from './ap_degree_map_query_helper';
import { APTermCourseDataQueryhelper } from './ap_term_course_data_query_helper';

export interface IAPTermsQueryResult {
  terms: APTerm[];
  plannedTerms: APTerm[];
  futureTerms: APTerm[];
  unPlannedTerms: APTerm[];
  unPlannedTermsDropdownOptions: Array<{ text: string; value: number }>;
  hasUnPlannedTerms: boolean;
  enableLockAllPlannedCoursesOption: boolean;
  enableUnlockAllPlannedCoursesOption: boolean;
}

export class APTermsQuery implements IQuery {
  private __result: IAPTermsQueryResult;

  constructor(
    QueryMediator: IQueryMediator,
    private ngAPTermsDatasource: IAPTermsDatasource,
    private ngAPPlannedCoursesDatasource: IAPPlannedCourseDataSource,
    private ngAPPlannedCourseGroupDatasource: IAPPlannedCourseGroupDatasource,
    private ngAPCoursesDatasource: IAPCoursesDatasource,
    private ngAPModel: APModel,
    private ngLargeCourseGroupModel: LargeCourseGroupModel,
    private ngLargeCourseGroupSearchResultModel: LargeCourseGroupSearchResultModel,
    private ngAPTermCourseDataQueryhelper: APTermCourseDataQueryhelper,
    private ngAPDegreeMapQueryHelper: APDegreeMapQueryHelper,
    private ngCommentsDatasource: ICommentsDatasource,
    private appConfigModel: IApplicationConfiguration
  ) {
    QueryMediator.registerQuery(
      this,
      ngAPCoursesDatasource,
      ngAPTermsDatasource,
      ngAPPlannedCoursesDatasource,
      ngAPPlannedCourseGroupDatasource,
      ngAPModel,
      ngLargeCourseGroupModel,
      ngCommentsDatasource,
      ngLargeCourseGroupSearchResultModel
    );
  }

  getPlaceholderWildcardGenericSlotsForTerm(
    termId: APTerm['id'],
    _programSlotWithMeta: Array<APGenericSlot['programSlot']>
  ): WildcardSlot[] {
    const wildcardsForTerm = this.ngAPTermCourseDataQueryhelper.getPlannedWildcardCourseGroupsForTerm(termId);
    const programSlotWithMeta = _4.keyBy(APProgramSlotServant.getWildCardProgramSlots(_programSlotWithMeta), 'id');

    return wildcardsForTerm.map((wildcard) => {
      const isCourseFlagged = false;
      const wildcardPgmSlot = programSlotWithMeta[wildcard.program_slot];

      return APGenericSlotBuilder.build({
        title: `${!_4.isEmpty(wildcardPgmSlot.desc) ? wildcardPgmSlot.desc : wildcardPgmSlot.course_group}`,
        type: 'WILDCARD',
        isCourseFlagged,
        flagComment: undefined,
        termId,
        credits: 3,
        course: undefined,
        plannedCourse: undefined,
        isPlaceHolder: false,
        instructions: wildcardPgmSlot.instructions,
        isLockedByAdvisor: false,
        plannedCourseGroup: wildcard,
        programSlot: wildcardPgmSlot,
        status: '',
        isLockedByStatus: false,
        isDragEnabled: true,
        genericSlots: [],
        isPlanned: true,
        isPlannedInCurrentGroup: false,
        hoverTemplate: undefined,
        disableSlot: false
      });
    });
  }

  buildPlannedCourseGenericSlotForTerm(
    termId: APTerm['id'],
    plndCourse: CourseSlot['plannedCourse'],
    courseProgramSlot: CourseSlot['programSlot']
  ) {
    const { isCourseFlagged, flagComment } = this.ngAPDegreeMapQueryHelper._getPlannedFlaggingInfo(plndCourse);
    const isCoursePlanned = !_4.isEmpty(plndCourse);
    const isMinGradeFeatureEnabled = this.appConfigModel.isFeatureEnabled('academicplan-program_slot_min_grade');
    const isMinGradeMet = APMinGradeServant.isMinGradeMet(courseProgramSlot, plndCourse, isMinGradeFeatureEnabled);

    return APGenericSlotBuilder.build({
      type: 'COURSE',
      title: `${plndCourse._course.cd}  ${plndCourse._course.title}`,
      termId,
      isCourseFlagged,
      flagComment,
      course: plndCourse._course,
      plannedCourse: plndCourse,
      programSlot: courseProgramSlot,
      status: plndCourse.status,
      isLockedByStatus: APCourseServant.isLockedByStatus(plndCourse.status),
      isDragEnabled: !_4.isEmpty(plndCourse),
      shouldShowAddToTermMenu: this.showAddToTermMenu(isMinGradeMet, isCoursePlanned, plndCourse),
      isMinGradeMet,
      isCourseCompleted: this.isCourseCompleted(plndCourse),
      isPlanned: !_4.isEmpty(plndCourse),
      credits: plndCourse._course.credits,
      hoverTemplate: APCourseServant.buildHoverTemplate(plndCourse._course),
      courseOfferedProblemMessage: APCourseServant.courseOfferedProblemMessage(),
      isLockedByAdvisor: _4.get(plndCourse, 'is_locked', false),
      disableSlot: false,
      isPlannedSomewhere: false
    });
  }

  showAddToTermMenu(isMinGradeMet: boolean, isCoursePlanned: boolean, plannedCourse: APPlannedCourse) {
    if (this.isCourseCompleted(plannedCourse) && !isMinGradeMet) {
      return true;
    }
    return !isCoursePlanned || !APCourseServant.isLockedByStatus(plannedCourse.status);
  }

  isCourseCompleted(plannedCourse: APPlannedCourse) {
    return _4.includes(['COMPLETED'], _4.get(plannedCourse, 'status', ''));
  }

  buildPlannedWildcardGenericSlotsForTerm(
    termId: APTerm['id'],
    plndCourse: NonNullable<WildcardSlot['plannedCourse']>,
    pgmSlot: WildcardSlot['programSlot']
  ) {
    const groupGenericSlots = this.ngAPDegreeMapQueryHelper.constructGroupGenericSlots(pgmSlot, {}, plndCourse);

    const { isCourseFlagged, flagComment } = this.ngAPDegreeMapQueryHelper._getPlannedFlaggingInfo(plndCourse);

    return APGenericSlotBuilder.build({
      title: `${_4.get(plndCourse, '_course.cd', '')}  ${_4.get(plndCourse, '_course.title', '')}`,
      type: 'WILDCARD',
      isCourseFlagged,
      flagComment,
      termId,
      disableSlot: false,
      credits: _4.get(plndCourse, '_course.credits', 0),
      course: _4.head(pgmSlot._courses as APCourse[]),
      plannedCourse: plndCourse,
      isPlaceHolder: false,
      instructions: pgmSlot.instructions,
      isLockedByAdvisor: _4.get(plndCourse, 'is_locked', false),
      plannedCourseGroup: {} as APCourseGroup,
      programSlot: pgmSlot,
      status: plndCourse.status,
      isLockedByStatus: APCourseServant.isLockedByStatus(plndCourse.status),
      isDragEnabled: APCourseServant.isDragEnabled(plndCourse.status),
      genericSlots: [groupGenericSlots[0]],
      isPlanned: true,
      isPlannedInCurrentGroup: true,
      hoverTemplate: APPlannedCourseServant.buildPlannedCourseHoverTemplate(plndCourse, plndCourse._course)
    });
  }

  buildPlannedCourseGroupGenericSlotForTerm(
    termId: APTerm['id'],
    plndCourse: NonNullable<CourseGroupSlot['plannedCourse']>,
    pgmSlot: CourseGroupSlot['programSlot']
  ) {
    const groupGenericSlots = this.ngAPDegreeMapQueryHelper.constructGroupGenericSlots(pgmSlot, {}, plndCourse);

    const { isCourseFlagged, flagComment } = this.ngAPDegreeMapQueryHelper._getPlannedFlaggingInfo(plndCourse);

    return APGenericSlotBuilder.build({
      title: `${plndCourse._course.cd} ${plndCourse._course.title}`,
      type: 'GROUP',
      isCourseFlagged,
      instructions: pgmSlot.desc,
      flagComment,
      termId,
      courses: pgmSlot._courses as APCourse[],
      plannedCourse: plndCourse,
      isPlaceHolder: false,
      disableSlot: false,
      isLockedByAdvisor: _4.get(plndCourse, 'is_locked', false),
      plannedCourseGroup: {} as APCourseGroup,
      programSlot: pgmSlot,
      status: plndCourse.status,
      isLockedByStatus: APCourseServant.isLockedByStatus(plndCourse.status),
      isDragEnabled: APCourseServant.isDragEnabled(plndCourse.status),
      firstCourse: plndCourse._course,
      genericSlots: _4.take(groupGenericSlots, Math.min(pgmSlot.total_courses, 10)),
      isPlanned: true,
      min_credits: pgmSlot.min_credits,
      max_credits: pgmSlot.max_credits,
      isPlannedInCurrentGroup: true,
      hoverTemplate: APPlannedCourseServant.buildPlannedCourseHoverTemplate(plndCourse, plndCourse._course)
    });
  }

  getCourseGroupPlaceholderGenericSlotsForTerm(
    termId: APTerm['id'],
    plndCourseGroup: CourseGroupSlot['plannedCourseGroup'],
    placeholderProgramSlot: CourseGroupSlot['programSlot']
  ) {
    const groupGenericSlots = this.ngAPDegreeMapQueryHelper.constructGroupGenericSlots(placeholderProgramSlot);

    return APGenericSlotBuilder.build({
      title: `${placeholderProgramSlot.desc} placeholder`,
      type: 'GROUP',
      termId,
      instructions: placeholderProgramSlot.desc,
      courses: placeholderProgramSlot._courses as APCourse[],
      isLockedByAdvisor: false,
      isPlaceHolder: true,
      isLockedByStatus: false,
      isDragEnabled: true,
      isPlanned: true,
      isPlannedInCurrentGroup: false,
      plannedCourseGroup: plndCourseGroup,
      programSlot: placeholderProgramSlot,
      status: '',
      disableSlot: false,
      max_credits: placeholderProgramSlot.max_credits,
      min_credits: placeholderProgramSlot.min_credits,
      firstCourse: (placeholderProgramSlot._courses as APCourse[])[0],
      genericSlots: _4.take(groupGenericSlots, Math.min(placeholderProgramSlot.total_courses, 10))
    });
  }

  getCourseGenericSlotsForTerm(term: APTerm) {
    const { programSlotWithMeta: _programSlotWithMeta } = this.ngAPTermCourseDataQueryhelper.getRequiredDataForTerms();
    const preReqProgramSlotWithMeta = _4.filter(_programSlotWithMeta, (prgmSlot: APProgramSlot) => prgmSlot.slot < 1);

    const programSlotWithMeta = APProgramSlotServant.getCourseRelatedProgramSlots(_programSlotWithMeta);

    return _4.map(term._plannedCourses, (plndCourse: APPlannedCourse & IAPPlannedCourseMeta) => {
      if (_4.isEmpty(plndCourse.program_slot)) {
        _4.each(preReqProgramSlotWithMeta, (slot: any) => {
          if (slot.is_devreq && _4.find(slot.courses, { course_id: plndCourse.course })) {
            plndCourse.program_slot = slot.id;
          }
        });
      }

      const courseProgramSlot = _4.find(programSlotWithMeta, {
        id: plndCourse.program_slot
      });

      if (_4.isUndefined(courseProgramSlot)) {
        return this.buildPlannedCourseGenericSlotForTerm(
          term.id,
          plndCourse,
          {} as SingleCourseProgramSlot & IAPProgramSlotMeta
        );
      }
      if (APProgramSlotServant.isCourseProgramSlot(courseProgramSlot)) {
        return this.buildPlannedCourseGenericSlotForTerm(term.id, plndCourse, courseProgramSlot);
      } else if (APProgramSlotServant.isWildcardProgramSlot(courseProgramSlot)) {
        return this.buildPlannedWildcardGenericSlotsForTerm(term.id, plndCourse, courseProgramSlot);
      } else {
        return this.buildPlannedCourseGroupGenericSlotForTerm(term.id, plndCourse, courseProgramSlot);
      }
    });
  }

  /* tslint:disable:cognitive-complexity */

  /* tslint:disable:no-big-function */
  /* FIX ME : split in to smaller functions*/

  run(): this {
    const { plannedCoursesWithMeta, programSlotWithMeta, termsWithMeta, futureTerms, plannedTerms, unPlannedTerms } =
      this.ngAPTermCourseDataQueryhelper.getRequiredDataForTerms();

    const unPlannedTermsDropdownOptions = _4.map(unPlannedTerms, (term: APTerm) => ({
      text: term.name,
      value: term.id
    }));

    _4.each(plannedTerms, (term: APTerm) => {
      const plannedCoursesTermsByTermIds = _4.groupBy(plannedCoursesWithMeta, 'term');

      const plannedCoursesForTerm = plannedCoursesTermsByTermIds[term.id];

      term._plannedCourses = plannedCoursesForTerm as APPlannedCourse[];
      term._plannedCourseGroups = this.ngAPTermCourseDataQueryhelper.getPlannedCourseGroupsForTerm(term.id);

      const wildcardGenericSlots = this.getPlaceholderWildcardGenericSlotsForTerm(term.id, programSlotWithMeta);

      const NCRsForTerm = this.ngAPTermCourseDataQueryhelper.getPlannedNCRsForTerm(term.id);
      const NCRGenericSlots = NCRsForTerm.map((ncr) => {
        const NCRPgmSlot = _4.find(programSlotWithMeta, {
          id: ncr.program_slot
        }) as NCRProgramSlot & IAPProgramSlotMeta;
        return APGenericSlotBuilder.build({
          title: `${!_4.isEmpty(NCRPgmSlot.desc) ? NCRPgmSlot.desc : NCRPgmSlot.course_group}`,
          type: 'NCR',
          instructions: NCRPgmSlot.instructions,
          isDragEnabled: true,
          isPlanned: true,
          status: 'PLANNED',
          termId: term.id,
          plannedCourseGroup: ncr,
          programSlot: NCRPgmSlot,
          disableSlot: false
        });
      });

      term._programSlots = _4.compact(
        _4.map(plannedCoursesForTerm, (course) =>
          _4.find(programSlotWithMeta, (slot) => slot.id === course.program_slot)
        )
      );

      term._genericSlots = [];

      const courseGenericSlots = this.getCourseGenericSlotsForTerm(term);

      const placeholderPgmSlots = _4.keyBy(APProgramSlotServant.getCourseGroupProgramSlots(programSlotWithMeta), 'id');

      const placeholderGenericSlots = _4.map(
        term._plannedCourseGroups,
        (plndCourseGroup: APCourseGroup & IAPCourseGroupMeta) =>
          this.getCourseGroupPlaceholderGenericSlotsForTerm(
            term.id,
            plndCourseGroup,
            placeholderPgmSlots[plndCourseGroup.program_slot]
          )
      );

      term._genericSlots = _4.concat(courseGenericSlots, placeholderGenericSlots, wildcardGenericSlots);
      term._genericSlots = term._genericSlots.concat(NCRGenericSlots);
      term._statusGroups = APTermServant.sortCourseBucketCategories(term._genericSlots, term);
      term._hasCoursesWithStatus = APTermServant.hasCoursesWithStatus(term);
      term._creditsRange = APProgramSlotServant.getCreditsRange(term._genericSlots);
      const plannedCoursesCount = _4.size(APTermServant.getSectionPickedAndPlannedCourses(term));
      const lockedPlannedCoursesCount = _4.size(APTermServant.getAllLockedPlannedCourses(term));
      term._showUnLockAllCourses = _4.gt(lockedPlannedCoursesCount, 0);
      term._showLockAllCourses = _4.gt(plannedCoursesCount, lockedPlannedCoursesCount);
      term._hasCreditCapExceeded = APTermServant.hasCreditCapExceeded(term);
      term._isLockedByAdvisorCoursePresent = APTermServant.hasAdvisorLockedCourseExist(term);
    });
    const enableLockAllPlannedCoursesOption = _4.some(plannedTerms, '_showLockAllCourses');
    const enableUnlockAllPlannedCoursesOption = _4.some(plannedTerms, '_showUnLockAllCourses');
    this.__result = {
      terms: termsWithMeta,
      plannedTerms,
      futureTerms,
      unPlannedTerms,
      unPlannedTermsDropdownOptions,
      hasUnPlannedTerms: _4.gt(_4.size(unPlannedTermsDropdownOptions), 0),
      enableLockAllPlannedCoursesOption,
      enableUnlockAllPlannedCoursesOption
    };

    return this;
  }

  get result(): IAPTermsQueryResult {
    return this.__result;
  }
}

angular.module('capability-ng-academic-planner').service('ngAPTermsQuery', APTermsQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/plan/queries/ap_terms_query.ts